import React, { useCallback, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import KendoActionsCell from '@components/Kendo/ActionsCell';
import { Dialog } from '@progress/kendo-react-dialogs';
import I18n, { translate } from '@utils/i18n';
import { editDeficiencyPath, deficiencyPath, unresolveDeficiencyPath } from 'routes';
import api from '@utils/axios';
import { Button } from '@ui';
import ColumnMenuContext from './ColumnMenuContext.tsx';
import ResolveModal from '../../components/ResolveModal/ResolveModal.tsx';
import redirectPath from '../helpers/redirectPath';

const namespace = 'features.deficiencies_index.actions_cell';

function ActionsCell({ colSpan, dataItem, className, ariaColumnIndex, style }) {
  const { screen, screenId, resolutionStatusesForSelect, afterSubmit } = useContext(ColumnMenuContext);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [resolveDialogVisible, setResolveDialogVisible] = useState(false);

  const authToken = useMemo(() => document.querySelector('meta[name="csrf-token"]').getAttribute('content'), []);

  const onUnresolve = useCallback(() => {
    api
      .post(unresolveDeficiencyPath({ id: dataItem.id, format: 'json', _options: true }))
      .then(() => window.location.reload());
  }, []);

  const onDelete = useCallback(() => toggleDeleteDialog(), []);
  const onResolve = useCallback(() => toggleResolveDialog(), []);

  const toggleDeleteDialog = useCallback(() => setDeleteDialogVisible((prev) => !prev), []);
  const toggleResolveDialog = useCallback(() => setResolveDialogVisible((prev) => !prev), []);

  const actionsList = useMemo(() => {
    const list = [];
    if (dataItem.isResolved) {
      list.push({
        title: translate('unresolve', { namespace }),
        faClass: 'fa-plus',
        onClick: onUnresolve
      });
    } else {
      list.push({
        title: translate('resolve', { namespace }),
        faClass: 'fa-square-check',
        onClick: onResolve
      });
    }

    list.push({
      title: translate('edit', { namespace }),
      faClass: 'fa-pen-to-square',
      link: editDeficiencyPath({ id: dataItem.id, redirect_to: redirectPath({ screen, screenId }), _options: true })
    });

    list.push({
      title: translate('delete', { namespace }),
      faClass: 'fa-trash-can',
      onClick: onDelete
    });

    return list;
  }, []);

  const deletePath = useMemo(
    () => deficiencyPath({ id: dataItem.id, redirect_path: redirectPath({ screen, screenId }), _options: true }),
    []
  );

  return (
    <>
      <KendoActionsCell
        colSpan={colSpan}
        className={className}
        ariaColumnIndex={ariaColumnIndex}
        style={style}
        actionsList={actionsList}
      />

      {deleteDialogVisible && (
        <Dialog title={translate('confirm', { namespace })} className="qmb-dialog" onClose={toggleDeleteDialog}>
          <p>{translate('confirm_delete', { namespace })}</p>
          <div className="modal__footer pull-right" style={{ justifyContent: 'space-between' }}>
            <form action={deletePath} method="POST">
              <input type="hidden" value="delete" name="_method" />
              <input name="authenticity_token" type="hidden" value={authToken} />
              <Button onClick={toggleDeleteDialog}>{I18n.t('generic.cancel')}</Button>
              <Button type="submit" variant="primary">
                {I18n.t('generic.ok')}
              </Button>
            </form>
          </div>
        </Dialog>
      )}

      {resolveDialogVisible && (
        <ResolveModal
          toggleDialog={toggleResolveDialog}
          deficiencyId={dataItem.id}
          resolutionStatusesForSelect={resolutionStatusesForSelect}
          onFinish={afterSubmit}
        />
      )}
    </>
  );
}

ActionsCell.propTypes = {
  ariaColumnIndex: PropTypes.number.isRequired,
  style: PropTypes.object,
  className: PropTypes.string,
  colSpan: PropTypes.number.isRequired,
  dataItem: PropTypes.shape({
    isResolved: PropTypes.bool,
    photos: PropTypes.arrayOf(PropTypes.object),
    path: PropTypes.string,
    id: PropTypes.number
  }).isRequired
};

ActionsCell.defaultProps = {
  style: null,
  className: ''
};

export default ActionsCell;
